import React, { useEffect, useState } from "react";
import { Box, Stack, Typography, TextField, Button, Alert, Card, CardContent } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useApi } from "../api";
import PageBreadcrumbs from "../components/Breadcrumbs";

type FieldErrors = Partial<Record<"oldPassword" | "newPassword" | "confirmPassword", string>>;

type PasswordPolicy = {
  minLength?: number;
  hasUppercase?: boolean;
  hasLowercase?: boolean;
  hasNumber?: boolean;
  hasSymbol?: boolean;
};

const UserSettings: React.FC = () => {
  const { t } = useTranslation();
  const api = useApi();

  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [passwordPolicy, setPasswordPolicy] = useState<PasswordPolicy | null>(null);
  const [passwordPolicyError, setPasswordPolicyError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    const fetchPasswordPolicy = async () => {
      try {
        const response = await api.get("/me/password-policy");
        const policy = response?.policy;

        if (!isMounted || !policy) {
          return;
        }

        const parsedMinLength = Number(policy.minLength);

        setPasswordPolicy({
          minLength: Number.isFinite(parsedMinLength) ? parsedMinLength : undefined,
          hasUppercase: policy.hasUppercase ?? undefined,
          hasLowercase: policy.hasLowercase ?? undefined,
          hasNumber: policy.hasNumber ?? undefined,
          hasSymbol: policy.hasSymbol ?? undefined,
        });
        setPasswordPolicyError(null);
      } catch (err) {
        console.error("Failed to load password policy", err);
        if (!isMounted) {
          return;
        }
        setPasswordPolicyError(t("userSettings.password.errors.policy"));
      }
    };

    fetchPasswordPolicy();

    return () => {
      isMounted = false;
    };
  }, [api, t]);

  const validatePasswords = (
    currentOldPassword: string,
    currentNewPassword: string,
    currentConfirmPassword: string,
  ): FieldErrors => {
    const errors: FieldErrors = {};

    if (
      currentNewPassword &&
      currentConfirmPassword &&
      currentNewPassword !== currentConfirmPassword
    ) {
      errors.confirmPassword = t("userSettings.password.errors.mismatch");
    }

    if (
      currentOldPassword &&
      currentNewPassword &&
      currentOldPassword === currentNewPassword
    ) {
      errors.newPassword = t("userSettings.password.errors.sameAsOld");
    }

    if (passwordPolicy && currentNewPassword) {
      const policyErrors: string[] = [];

      if (
        passwordPolicy.minLength !== undefined &&
        currentNewPassword.length < passwordPolicy.minLength
      ) {
        policyErrors.push(
          t("userSettings.password.errors.minLength", { count: passwordPolicy.minLength }),
        );
      }

      if (passwordPolicy.hasUppercase && !/[A-Z]/.test(currentNewPassword)) {
        policyErrors.push(t("userSettings.password.errors.uppercase"));
      }

      if (passwordPolicy.hasLowercase && !/[a-z]/.test(currentNewPassword)) {
        policyErrors.push(t("userSettings.password.errors.lowercase"));
      }

      if (passwordPolicy.hasNumber && !/[0-9]/.test(currentNewPassword)) {
        policyErrors.push(t("userSettings.password.errors.number"));
      }

      if (passwordPolicy.hasSymbol && !/[^\w\s]/.test(currentNewPassword)) {
        policyErrors.push(t("userSettings.password.errors.symbol"));
      }

      if (policyErrors.length > 0) {
        const existingMessage = errors.newPassword ? [errors.newPassword] : [];
        errors.newPassword = [...existingMessage, ...policyErrors].join(" ");
      }
    }

    return errors;
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSuccessMessage(null);
    setErrorMessage(null);

    const validationErrors = validatePasswords(oldPassword, newPassword, confirmPassword);
    if (Object.keys(validationErrors).length > 0) {
      setFieldErrors(validationErrors);
      return;
    }

    setFieldErrors({});
    setIsSubmitting(true);

    try {
      await api.post("/me/set-password", {
        old_password: oldPassword,
        new_password: newPassword,
      });

      setSuccessMessage(t("userSettings.password.messages.success"));
      setOldPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      console.error("Password change failed", err);
      setErrorMessage(t("userSettings.password.messages.error"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOldPasswordChange = (value: string) => {
    setOldPassword(value);
    const errors = validatePasswords(value, newPassword, confirmPassword);
    setFieldErrors(errors);
  };

  const handleNewPasswordChange = (value: string) => {
    setNewPassword(value);
    const errors = validatePasswords(oldPassword, value, confirmPassword);
    setFieldErrors(errors);
  };

  const handleConfirmPasswordChange = (value: string) => {
    setConfirmPassword(value);
    const errors = validatePasswords(oldPassword, newPassword, value);
    setFieldErrors(errors);
  };

  return (
    <Box sx={{ maxWidth: 560, mx: "auto", mt: 4, px: 2 }}>
      <PageBreadcrumbs
        items={[
          { label: t("nav.dashboard"), path: "/dashboard" },
          { label: t("userSettings.title") },
        ]}
      />
      <Stack spacing={4}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            {t("userSettings.title")}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {t("userSettings.description")}
          </Typography>
        </Box>

        <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
          <CardContent sx={{ p: 3 }}>
        <Box
          component="form"
          onSubmit={handleSubmit}
        >
          <Stack spacing={3}>
            <Box>
              <Typography variant="h6" component="h2" gutterBottom>
                {t("userSettings.password.sectionTitle")}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t("userSettings.password.sectionDescription")}
              </Typography>
            </Box>

            {successMessage && (
              <Alert severity="success" onClose={() => setSuccessMessage(null)}>
                {successMessage}
              </Alert>
            )}

            {errorMessage && (
              <Alert severity="error" onClose={() => setErrorMessage(null)}>
                {errorMessage}
              </Alert>
            )}

            {passwordPolicyError && (
              <Alert severity="warning" onClose={() => setPasswordPolicyError(null)}>
                {passwordPolicyError}
              </Alert>
            )}

            <TextField
              type="password"
              label={t("userSettings.password.oldPasswordLabel")}
              value={oldPassword}
              onChange={(event) => handleOldPasswordChange(event.target.value)}
              required
              fullWidth
              autoComplete="current-password"
              error={Boolean(fieldErrors.oldPassword)}
              helperText={fieldErrors.oldPassword}
            />

            <TextField
              type="password"
              label={t("userSettings.password.newPasswordLabel")}
              value={newPassword}
              onChange={(event) => handleNewPasswordChange(event.target.value)}
              required
              fullWidth
              autoComplete="new-password"
              error={Boolean(fieldErrors.newPassword)}
              helperText={fieldErrors.newPassword}
            />

            <TextField
              type="password"
              label={t("userSettings.password.confirmPasswordLabel")}
              value={confirmPassword}
              onChange={(event) => handleConfirmPasswordChange(event.target.value)}
              required
              fullWidth
              autoComplete="new-password"
              error={Boolean(fieldErrors.confirmPassword)}
              helperText={fieldErrors.confirmPassword}
            />

            <Box display="flex" justifyContent="flex-end">
              <Button
                type="submit"
                variant="contained"
                disabled={isSubmitting}
              >
                {isSubmitting
                  ? t("userSettings.password.actions.submitting")
                  : t("userSettings.password.actions.submit")}
              </Button>
            </Box>
          </Stack>
        </Box>
          </CardContent>
        </Card>
      </Stack>
    </Box>
  );
};

export default UserSettings;
